import { AbsoluteFill, Img, staticFile } from "remotion";
import { FontFaces } from "./fonts.tsx";
import { QUOT, SPEAKER_STILL_FOUNDING_WIDTH, SPEAKER_STILL_WIDTH, type FontFaceSpec } from "./types.ts";

/** Optional decorative arrow drawn over the speaker card. Positions are
 *  relative to the FOUNDING 1080×1350 design space (existing channel configs
 *  predate the canvas widening); widthPct converts to px against 1080. */
export interface SpeakerArrowSpec {
  /** staticFile-relative path under public/, e.g. "channel-arrows/qb.png". */
  src: string;
  widthPct: number;
  topPx: number;
  leftPx: number;
}

export interface SpeakerCardStillProps extends Record<string, unknown> {
  /** Arabic display name, e.g. "جاي شيتي" */
  name: string;
  /** 1–2 short description lines */
  description: string;
  /** Name color (legacy gold). */
  nameColor?: string;
  /** Description color (legacy cream). */
  descColor?: string;
  /** Font family for both lines (legacy "Faseh"). */
  font?: string;
  /** @font-face registrations (legacy AlHurra/Faseh/Lalezar). */
  fontFaces?: FontFaceSpec[];
  /** Optional brand arrow overlay. */
  arrow?: SpeakerArrowSpec | null;
  /** Text/reading direction. Omitted → "rtl" so legacy/Arabic renders are
   *  byte-identical (right-pinned, right-aligned). "ltr" pins + aligns left. */
  direction?: "rtl" | "ltr";
  /** Font for the NAME line only. Omitted → the name uses `font`, i.e. the
   *  historical single-font card. Lets a channel run a display face on the
   *  name while the role line keeps the body face (QB/GHAITHSANS, 2026-09-03). */
  nameFont?: string;
  /** Per-channel cap for the name size (founding NAME_FONT_PX = 230). A
   *  creator-style small serif card runs ~115. Omitted → the founding cap,
   *  byte-identical. The adaptive shrink still applies UNDER this cap. */
  nameFontPx?: number;
}

/** Uniform name size — the operator-validated "Alex Hormozi" reference
 *  (2026-08-13): every card's name renders at this size, long or short.
 *  Calibrated against the hand-prepped alex-hormozi.png the operator pointed
 *  at (gold glyph run measures 215px tall on the 1080 canvas): Faseh-Bold's
 *  measured bbox factor is ~1.06 × fontSize, so 203 reproduces that height.
 *  Do NOT re-derive this from the old adaptive formula — the formula's 153
 *  for that name is the SMALL look the operator rejected. */
const NAME_FONT_PX = 230;
/** Text budget on the widened canvas (80px padding both sides). Long names
 *  extend toward frame center up to this width before anything shrinks. */
const TEXT_MAX_WIDTH = SPEAKER_STILL_WIDTH - 160;

/**
 * Name font size: the uniform NAME_FONT_PX for everyone — the widened canvas
 * gives long names room to grow toward frame center on one line instead of
 * shrinking (pre-2026-08-13 behaviour: 220px caps for short names, 80px
 * floors for long ones — the operator rejected the size lottery). Only a
 * name too long even for the widened budget shrinks to fit, and the box
 * still never clips (ADR-061): an under-estimate at worst wraps.
 *
 * Ratio history: 0.30 clipped "كريستن هولمز" (2026-05-04), 0.36 clipped
 * "جيسي إينشاوسـ..." (2026-07-29); 0.44 buys headroom over real Faseh-Bold
 * advance (≥ ~0.38em for wide mixes). Latin bold-sans runs wider (~0.52em).
 */
export function adaptiveNameFontSize(name: string, isLtr = false, capPx: number = NAME_FONT_PX): number {
  const charWidthRatio = isLtr ? 0.52 : 0.44;
  // Floors raised 80→175 (rtl) / 64→140 (ltr) on 2026-09-03. The floor is
  // really a "wrap rather than shrink past this" threshold: the box never
  // clips (ADR-061), so a name too long for one line at the floor just wraps
  // to a second line at full size instead of shrinking to fit one. The old
  // 80px floor produced the cards the operator flagged as small and noisy —
  // at 80px the name is ~28px on a 1080-wide frame once the card is placed at
  // 35%, and x264 turns glyphs that small into mush. Two big lines beat one
  // unreadable line; vertical room is not the constraint (the text block uses
  // ~25% of the 1350px canvas).
  // A per-channel cap BELOW the floor must win: a brand whose card runs a
  // small 115px serif can't have the 175 floor push it back up. min() the
  // floor against the cap so the cap is the hard ceiling in both directions.
  const minSize = Math.min(isLtr ? 140 : 175, capPx);
  const chars = Math.max(name.length, 1);
  const oneLineFit = Math.floor(TEXT_MAX_WIDTH / (chars * charWidthRatio));
  return Math.max(minSize, Math.min(capPx, oneLineFit));
}

/** The description must always read as SUBORDINATE to the name — operator rule
 *  (2026-09-03). Before this, nothing coupled the two: the name floor (80) sat
 *  BELOW the description cap (115), so a long name + short role line rendered
 *  the role BIGGER than the person. Cap the description at this fraction of
 *  whatever the name actually resolved to. */
const DESC_MAX_FRACTION_OF_NAME = 0.7;

/** Description wrap budget: narrower than the name's so the cream role line
 *  stays a compact block under the name instead of a low-contrast streak
 *  toward frame center. */
const DESC_MAX_WIDTH = 1200;

/**
 * Description aims to fit ≤2 lines without overflowing the card. Tightened
 * (2026-04-30) so longer descriptions (4+ noun phrases like "psychologist.
 * author. speaker. consultant.") don't push the third line past the canvas
 * bottom. Cap 96 → 115 with the 2026-08-13 recalibration: the hand-prepped
 * reference card's cream line measures 97px tall ≈ Faseh-Bold at ~115.
 */
function adaptiveDescriptionFontSize(
  description: string,
  isLtr = false,
  nameFontPx: number = NAME_FONT_PX,
): number {
  const targetWidth = DESC_MAX_WIDTH;
  // See adaptiveNameFontSize: rtl → 0.34 (Arabic); ltr → wider Latin ratio so
  // a French role line ("animateur de podcast. auteur.") fits in ≤2 lines.
  const charWidthRatio = isLtr ? 0.5 : 0.34;
  // Cap 115→130, floor 64→76 (2026-09-03): same "too small to survive x264"
  // complaint as the name.
  const maxSize = 130;
  const minSize = 76;
  const chars = Math.max(description.trim().length, 1);
  // Allow up to two lines before shrinking — divide effective char budget.
  const perLine = Math.ceil(chars / 2);
  const fitted = Math.max(minSize, Math.min(maxSize, Math.floor(targetWidth / (perLine * charWidthRatio))));
  // The subordination cap OUTRANKS the floor: when a very long name shrank,
  // the description follows it down rather than overtaking it. Guaranteed
  // strictly smaller so the two can never render at the same size either.
  const ceiling = Math.max(1, Math.floor(nameFontPx * DESC_MAX_FRACTION_OF_NAME));
  return Math.min(fitted, ceiling, nameFontPx - 1);
}

/**
 * A still PNG composition used to auto-generate a speaker card when one is
 * missing from speakers-names/. Output should match the visual style of the
 * existing Canva-exported PNGs: transparent background, yellow Faseh-Bold
 * name on top, smaller cream Faseh-Bold description below.
 *
 * Canvas chosen to match the typical 1080x1080 export the user uses on Canva,
 * but with a transparent alpha channel.
 */
export const SpeakerCardStill = ({
  name,
  description,
  nameColor,
  descColor,
  font,
  fontFaces,
  arrow,
  direction,
  nameFont,
  nameFontPx: nameFontPxProp,
}: SpeakerCardStillProps): React.JSX.Element => {
  const fontStack = `${font ?? "Faseh"}, sans-serif`;
  // Falls back to the description's stack, so a channel that never sets
  // nameFont renders exactly as before.
  const nameFontStack = `${nameFont ?? font ?? "Faseh"}, sans-serif`;
  // Omitted → "rtl": Arabic/legacy stays right-pinned + right-aligned,
  // byte-identical. "ltr" pins the block left and left-aligns the text.
  const dir = direction ?? "rtl";
  const isLtr = dir === "ltr";
  // Resolve the name size FIRST: the description is sized relative to it so it
  // can never out-rank the person's name (DESC_MAX_FRACTION_OF_NAME).
  const nameFontPx = adaptiveNameFontSize(name, isLtr, nameFontPxProp ?? NAME_FONT_PX);
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "transparent",
        justifyContent: "center",
        // Left-pinned for BOTH directions (2026-08-13): the card sits on the
        // frame's left, so extra name length must grow RIGHT, toward frame
        // center — the old rtl right-pin grew long names toward (and off)
        // the left frame edge. Text inside stays right-aligned for Arabic.
        alignItems: "flex-start",
        padding: 80,
        overflow: "hidden",
      }}
    >
      <FontFaces faces={fontFaces} />
      {arrow && (
        <Img
          src={staticFile(arrow.src)}
          style={{
            position: "absolute",
            top: arrow.topPx,
            left: arrow.leftPx,
            // widthPct is % of the FOUNDING design space — px against 1080,
            // not the widened canvas, so existing channel arrows keep their
            // exact pre-widening size.
            width: (arrow.widthPct / 100) * SPEAKER_STILL_FOUNDING_WIDTH,
            height: "auto",
          }}
        />
      )}
      <div
        dir={dir}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 20,
          textAlign: isLtr ? "left" : "right",
          maxWidth: TEXT_MAX_WIDTH,
        }}
      >
        <div
          style={{
            fontFamily: nameFontStack,
            fontWeight: 700,
            color: nameColor ?? QUOT.GOLD,
            fontSize: nameFontPx,
            lineHeight: 1.05,
            // NEVER truncate — both directions. The adaptive size shrinks the
            // full name to fit one line; a 2-line wrap is the last resort for
            // extreme lengths, an ellipsis never. (The rtl ellipsis box
            // produced "جيسي إينشاوسـ..." on real cards whenever the
            // char-width estimate under-measured Faseh-Bold — 2026-07-29.)
            whiteSpace: "normal",
            textShadow: "0 6px 18px rgba(0,0,0,0.6)",
            maxWidth: TEXT_MAX_WIDTH,
            overflow: "visible",
            textOverflow: "clip",
          }}
        >
          {name}
        </div>
        {/* descriptionWords: 0 blanks the role line upstream — render NOTHING
            rather than an empty box, so the gap above it goes too and the card
            is a name on its own (the creator-reel look). */}
        {description.trim().length > 0 && (
        <div
          style={{
            fontFamily: fontStack,
            fontWeight: 700,
            color: descColor ?? QUOT.CREAM,
            fontSize: adaptiveDescriptionFontSize(description, isLtr, nameFontPx),
            lineHeight: 1.2,
            maxWidth: DESC_MAX_WIDTH,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            textShadow: "0 4px 12px rgba(0,0,0,0.55)",
            // -webkit-box is kept ONLY for line metrics (fitting cards render
            // pixel-identical to the founding cards); the clamp + hidden
            // overflow are gone: the adaptive size targets ≤2 lines, but when
            // a description runs long anyway the 3rd line must SHOW — the old
            // WebkitLineClamp silently swallowed it mid-sentence with a "…"
            // (same operator rule as the name: shrink or wrap, never truncate).
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
          }}
        >
          {description}
        </div>
        )}
      </div>
    </AbsoluteFill>
  );
};
